import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Eye, EyeOff, Check, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import { adminSignup } from "@/services/admin";
import { resetPassword } from "@/services/auth";

const resetPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(1, "Password is required")
      .min(8, "Password must be at least 8 characters")
      .max(32, "Password must be at most 32 characters")
      .refine((pw) => /[a-z]/.test(pw), "Password must include at least one lowercase letter")
      .refine((pw) => /[A-Z]/.test(pw), "Password must include at least one uppercase letter")
      .refine((pw) => /[0-9]/.test(pw), "Password must include at least one number")
      .refine((pw) => /[^a-zA-Z0-9]/.test(pw), "Password must include at least one special character"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      token: (search.token as string) || "",
      slug: (search.slug as string) || "",
      stytch_token_type: (search.stytch_token_type as string) || "",
      type: (search.type as string) || "",
    };
  },
});

function PasswordRequirement({
  met,
  label,
}: {
  met: boolean;
  label: string;
}) {
  return (
    <li className={`flex items-center gap-1.5 text-xs ${met ? "text-green-500" : "text-muted-foreground"}`}>
      {met ? <Check size={12} /> : <X size={12} />}
      {label}
    </li>
  );
}

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordValue, setPasswordValue] = useState("");
  const search = Route.useSearch();
  const token = search.token;
  // `type=forgot` is appended by the backend's forgot-password email link to
  // distinguish from the invitation-signup flow that shares this URL.
  const isForgotFlow = search.type === "forgot";

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const newPasswordField = register("newPassword");

  const hasMinLength = passwordValue.length >= 8;
  const hasLowercase = /[a-z]/.test(passwordValue);
  const hasUppercase = /[A-Z]/.test(passwordValue);
  const hasNumber = /[0-9]/.test(passwordValue);
  const hasSpecial = /[^a-zA-Z0-9]/.test(passwordValue);

  const showRequirements = passwordValue.length > 0;

  useEffect(() => {
    if (!token) {
      toast.error("Token is missing from the URL");
      navigate({ to: "/" });
    }
  }, [token, navigate]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      toast.error("Token is missing from the URL");
      return;
    }

    setIsLoading(true);
    try {
      const response = isForgotFlow
        ? await resetPassword({ token, password: data.newPassword })
        : await adminSignup({ token, password: data.newPassword });
      toast.success(response.message || "Password set successfully");
      navigate({ to: "/login" });
    } catch (error) {
      console.error("Reset password error:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Password reset failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: "url('/loginBG.webp')" }}
    >
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-2xl font-bold">
            {isForgotFlow ? "Reset Password" : "Set Password"}
          </CardTitle>
          <CardDescription>
            {isForgotFlow
              ? "Enter a new password for your account"
              : "Enter a password to complete your signup"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter your new password"
                  {...newPasswordField}
                  onChange={(e) => {
                    setPasswordValue(e.target.value);
                    newPasswordField.onChange(e);
                  }}
                  className={errors.newPassword ? "border-red-500 pr-10" : "pr-10"}
                  disabled={isLoading}
                  autoComplete="new-password"
                  maxLength={32}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {showRequirements && (
                <ul className="space-y-1 pt-1">
                  <PasswordRequirement met={hasMinLength} label="At least 8 characters" />
                  <PasswordRequirement met={hasLowercase} label="At least one lowercase letter" />
                  <PasswordRequirement met={hasUppercase} label="At least one uppercase letter" />
                  <PasswordRequirement met={hasNumber} label="At least one number" />
                  <PasswordRequirement met={hasSpecial} label="At least one special character" />
                </ul>
              )}
              {errors.newPassword && !showRequirements && (
                <p className="text-sm text-red-500">
                  {errors.newPassword.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your new password"
                  {...register("confirmPassword")}
                  className={errors.confirmPassword ? "border-red-500 pr-10" : "pr-10"}
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-red-500">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
